import { AppValidator } from "@/common/app-validate.ts";
import { LocalStorage } from "@/common/storage.ts";
import AuthLayout from "@/component/common/layout/AuthLayout.tsx";
import CustomRequiredMarkAntdForm from "@/component/CustomRequiredMarkAntdForm.tsx";
import { MANAGER_PATH } from "@/constant/url-path.ts";
import { AuthService } from "@/services/auth/AuthService.ts";
import {
    EyeInvisibleOutlined,
    EyeTwoTone,
    LockOutlined,
    UserOutlined,
} from "@ant-design/icons";
import { Button, Card, Checkbox, ConfigProvider, Form, Input } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const SignIn = () => {
  const { t } = useTranslation();
  const validators = AppValidator(t);
  const navigation = useNavigate();

  const [signInForm] = Form.useForm();

  const handleSignIn = async (values: SignInReq) => {
    LocalStorage.clearAuthenticationStorage();
    const res: any = await AuthService.signIn(values);
    if (res) {
      // Support both legacy body shape and new auth-api shape
      const accessToken = res.accessToken ?? res.access_token ?? null;
      const refreshToken = res.refreshToken ?? res.refresh_token ?? null;
      const expiresDate = res.expiresDate ?? res.expires_date ?? null;

      // Minimal user info fallback: front-end may fetch full profile later
      const userInfo = {
        username: values.username,
        ...(res.username ? { usernameFromRes: res.username } : {}),
      };

      LocalStorage.setUserInfo(userInfo);
      if (accessToken) LocalStorage.setAccessToken(accessToken);
      if (refreshToken) LocalStorage.setRefreshToken(refreshToken);
      if (expiresDate) LocalStorage.setExpiresDate(expiresDate);
      navigation(MANAGER_PATH.LICENSE_REGISTRATION.LIST, {
        state: { from: "LoginPage" },
      });
    }
  };

  return (
    <>
      <AuthLayout>
        <Card
          style={{
            position: "absolute",
            top: "20%",
            left: "50%",
            transform: "translate(-50%, 0%)",
            width: 480,
          }}
          styles={{ body: { padding: 40 } }}
        >
          <div className={"text-[1.5rem] font-bold text-center mb-10"}>
            SIGN IN
          </div>
          <Form
            form={signInForm}
            name="normal_login"
            className="login-form mb-0"
            initialValues={{
              remember: true,
            }}
            layout={"vertical"}
            requiredMark={CustomRequiredMarkAntdForm}
            onFinish={handleSignIn}
            size={"large"}
          >
            <Form.Item
              name="username"
              label={t("field.username")}
              rules={[validators.required(t("field.username"))]}
            >
              <Input
                prefix={<UserOutlined className="site-form-item-icon" />}
                placeholder={t("placeholder.enterYourUsername")}
              />
            </Form.Item>
            <Form.Item
              name="password"
              label={t("field.password")}
              rules={[
                validators.required(t("field.password")),
                validators.minLength(8, t("field.password")),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined className="site-form-item-icon" />}
                type="password"
                max={255}
                placeholder={t("placeholder.enterYourPassword")}
                iconRender={(visible: boolean) =>
                  visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                }
              />
            </Form.Item>
            <Form.Item>
              <Form.Item name="remember" valuePropName="checked" noStyle>
                <Checkbox>{t("field.rememberMe")}</Checkbox>
              </Form.Item>
            </Form.Item>

            <Form.Item>
              <ConfigProvider
                theme={{
                  components: {
                    Button: {
                      colorPrimary: "#4154F1",
                    },
                  },
                }}
              >
                <Button type="primary" htmlType="submit" className="w-full">
                  {t("button.signIn")}
                </Button>
              </ConfigProvider>
            </Form.Item>
          </Form>
          <div>
            <a className="" href={MANAGER_PATH.AUTH.FORGOT_PASSWORD}>
              {t("link.forgotPassword")}
            </a>
          </div>
        </Card>
      </AuthLayout>
    </>
  );
};

export default SignIn;
